import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import DateInput, { ageInYears, MIN_AGE } from "../components/DateInput";
import ThemeToggle from "../components/ThemeToggle";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const STEPS = ["Identity", "Birth Date", "Birth Time", "Birth Place", "Account"];

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        name: "",
        date_of_birth: "",
        time_of_birth: "",
        birth_city: "",
        birth_country: "",
        birth_state: "",
        birth_zip: "",
        birth_lat: "",
        birth_lng: "",
        email: "",
        password: "",
    });

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const ageOk = form.date_of_birth ? ageInYears(form.date_of_birth) >= MIN_AGE : false;

    const canNext = () => {
        if (step === 0) return form.name.trim().length >= 2;
        if (step === 1) return !!form.date_of_birth && ageOk;
        if (step === 2) return !!form.time_of_birth;
        if (step === 3) return form.birth_city.trim() && form.birth_country.trim();
        return true;
    };

    const next = (e) => {
        e?.preventDefault();
        if (step === 1 && form.date_of_birth && !ageOk) {
            setError(`You must be at least ${MIN_AGE} to use StarPath.`);
            return;
        }
        setError("");
        if (!canNext()) return;
        setStep((s) => Math.min(STEPS.length - 1, s + 1));
    };
    const back = () => setStep((s) => Math.max(0, s - 1));

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setBusy(true);
        try {
            const payload = {
                email: form.email,
                password: form.password,
                name: form.name,
                birth: {
                    date_of_birth: form.date_of_birth,
                    time_of_birth: form.time_of_birth,
                    birth_city: form.birth_city,
                    birth_country: form.birth_country,
                    birth_state: form.birth_state || null,
                    birth_zip: form.birth_zip || null,
                    birth_lat: form.birth_lat ? parseFloat(form.birth_lat) : null,
                    birth_lng: form.birth_lng ? parseFloat(form.birth_lng) : null,
                },
            };
            await register(payload);
            navigate("/dashboard");
        } catch (e2) {
            setError(e2.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-sand-50 text-ink">
            <header className="sp-container flex items-center justify-between h-20">
                <Link to="/" className="sp-overline" data-testid="register-back">← StarPath</Link>
                <div className="flex items-center gap-3">
                    <ThemeToggle />
                    <Link to="/login" className="text-sm text-ink-muted hover:text-ink" data-testid="register-to-login">
                        Sign in
                    </Link>
                </div>
            </header>

            <div className="max-w-lg mx-auto px-6 mt-12 pb-24">
                <div className="flex items-center gap-2 mb-10">
                    {STEPS.map((label, i) => (
                        <div key={label} className="flex items-center gap-2 flex-1">
                            <div
                                className={`h-px flex-1 ${i <= step ? "bg-ink" : "bg-ink/15"}`}
                                data-testid={`register-step-bar-${i}`}
                            />
                        </div>
                    ))}
                </div>
                <div className="sp-overline" data-testid="register-step-label">{step + 1} / {STEPS.length} — {STEPS[step]}</div>

                <form onSubmit={step === STEPS.length - 1 ? submit : next} className="mt-6 space-y-8">
                    {step === 0 && (
                        <>
                            <button type="button" onClick={startGoogleLogin} className="sp-btn-ghost w-full" data-testid="register-google">
                                <svg viewBox="0 0 24 24" className="w-4 h-4"><path fill="#4285F4" d="M23.49 12.27c0-.86-.07-1.5-.23-2.18H12v4.13h6.61c-.13 1.08-.86 2.72-2.47 3.82l-.02.15 3.59 2.78.25.02c2.28-2.11 3.53-5.21 3.53-8.72"/><path fill="#34A853" d="M12 24c3.24 0 5.95-1.07 7.94-2.91l-3.78-2.93c-1.01.71-2.37 1.2-4.16 1.2-3.18 0-5.87-2.1-6.83-5.01l-.14.01-3.74 2.89-.05.13C3.21 21.3 7.27 24 12 24"/><path fill="#FBBC05" d="M5.17 14.35c-.25-.74-.4-1.53-.4-2.35s.14-1.6.39-2.35l-.01-.16-3.78-2.94-.12.06C.48 8.18 0 10.04 0 12s.48 3.82 1.25 5.39l3.92-3.04"/><path fill="#EB4335" d="M12 4.75c2.26 0 3.78.98 4.65 1.79l3.4-3.32C17.95 1.26 15.24 0 12 0 7.27 0 3.21 2.7 1.25 6.61l3.91 3.04C6.13 6.85 8.82 4.75 12 4.75"/></svg>
                                Continue with Google
                            </button>
                            <div className="flex items-center gap-4 text-xs text-ink-muted">
                                <div className="h-px bg-ink/10 flex-1"/>
                                <span className="sp-overline">or with email</span>
                                <div className="h-px bg-ink/10 flex-1"/>
                            </div>
                            <div>
                                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif">What may we call you?</h1>
                                <p className="text-ink-soft mt-2">A name to anchor your chart.</p>
                                <input
                                    autoFocus
                                    type="text"
                                    required
                                    placeholder="Your name"
                                    value={form.name}
                                    onChange={update("name")}
                                    className="sp-input mt-8"
                                    data-testid="register-name"
                                />
                            </div>
                        </>
                    )}

                    {step === 1 && (
                        <div>
                            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif">When did you arrive?</h1>
                            <p className="text-ink-soft mt-2">Type your birthday — MM / DD / YYYY.</p>
                            <DateInput
                                autoFocus
                                required
                                value={form.date_of_birth}
                                onChange={update("date_of_birth")}
                                className="sp-input mt-8"
                                data-testid="register-date"
                            />
                            {form.date_of_birth && !ageOk && (
                                <p className="text-sm text-red-700 mt-3" data-testid="register-age-error">
                                    You must be at least {MIN_AGE} to use StarPath.
                                </p>
                            )}
                            <p className="text-xs text-ink-muted mt-3">Your Sun sign is calculated from this date.</p>
                        </div>
                    )}

                    {step === 2 && (
                        <div>
                            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif">At what hour?</h1>
                            <p className="text-ink-soft mt-2">As accurately as you know — this shapes your Moon &amp; Rising.</p>
                            <input
                                autoFocus
                                type="time"
                                required
                                value={form.time_of_birth}
                                onChange={update("time_of_birth")}
                                className="sp-input mt-8"
                                data-testid="register-time"
                            />
                            <p className="text-xs text-ink-muted mt-3">Not sure? Use noon — your Sun sign will still be accurate.</p>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-6">
                            <div>
                                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif">And where?</h1>
                                <p className="text-ink-soft mt-2">City and country of birth.</p>
                            </div>
                            <input
                                autoFocus
                                type="text"
                                required
                                placeholder="City"
                                value={form.birth_city}
                                onChange={update("birth_city")}
                                className="sp-input"
                                data-testid="register-city"
                            />
                            <input
                                type="text"
                                required
                                placeholder="Country"
                                value={form.birth_country}
                                onChange={update("birth_country")}
                                className="sp-input"
                                data-testid="register-country"
                            />
                            <div className="grid grid-cols-2 gap-4">
                                <input
                                    type="text"
                                    placeholder="State / region (optional)"
                                    value={form.birth_state}
                                    onChange={update("birth_state")}
                                    className="sp-input text-base"
                                    data-testid="register-state"
                                />
                                <input
                                    type="text"
                                    placeholder="Zip / postal (optional)"
                                    value={form.birth_zip}
                                    onChange={update("birth_zip")}
                                    className="sp-input text-base"
                                    data-testid="register-zip"
                                />
                            </div>
                            <p className="text-xs text-ink-muted">We&apos;ll auto-locate your coordinates from the city you provide.</p>
                        </div>
                    )}

                    {step === 4 && (
                        <div className="space-y-6">
                            <div>
                                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif">Create your account.</h1>
                                <p className="text-ink-soft mt-2">So we can keep your chart safe.</p>
                            </div>
                            <input
                                autoFocus
                                type="email"
                                required
                                placeholder="Email"
                                value={form.email}
                                onChange={update("email")}
                                className="sp-input"
                                data-testid="register-email"
                            />
                            <input
                                type="password"
                                required
                                minLength={6}
                                placeholder="Password (min 6 characters)"
                                value={form.password}
                                onChange={update("password")}
                                className="sp-input"
                                data-testid="register-password"
                            />
                        </div>
                    )}

                    {error && <div className="text-sm text-red-700" data-testid="register-error">{error}</div>}

                    <div className="flex items-center gap-3 pt-4">
                        {step > 0 && (
                            <button type="button" onClick={back} className="sp-btn-ghost" data-testid="register-back-step">Back</button>
                        )}
                        <button
                            type="submit"
                            disabled={!canNext() || busy}
                            className="sp-btn-primary"
                            data-testid={step === STEPS.length - 1 ? "register-submit" : "register-next"}
                        >
                            {step === STEPS.length - 1 ? (busy ? "Drawing your chart…" : "Reveal my chart") : "Continue"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
